import React, { useState, useEffect } from "react";
import styles from "./PromotionalPrompt.module.scss";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import {
  SecondaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { SaleTagIcon } from "@/components/__shared__/SaleTagIcon/SaleTagIcon";
import { HorizontalRule } from "@/components";
import TermsAndConditionsAccordion from "@/components/__shared__/TermsAndConditionsAccordion/TermsAndConditionsAccordion";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

type Props = {
  message: string;
  promoCode: string;
  terms: string | undefined;
  isBlackOverride: boolean;
};

export const PromotionalPromptUI = ({
  message,
  promoCode,
  terms,
  isBlackOverride,
}: Props) => {
  const [copyButtonIsClicked, setCopyButtonIsClicked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(promoCode?.length > 0);

  useEffect(() => {
    if (isModalOpen) dispatchMonetateQEvent("PROMOTION_PROMPT_MODAL_OPENED");
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const copyPromoCode = () => {
    navigator.clipboard.writeText(promoCode);
    setCopyButtonIsClicked(true);
    dispatchMonetateQEvent("COPY_PROMO_BUTTON_CLICKED");
  };

  const openBasket = () => {
    window.open("/basket", "_blank");
    dispatchMonetateQEvent("VIEW_BASKET_BUTTON_CLICKED");
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={closeModal}
      title="The items in your basket qualify for a special offer"
      rootId="page-root"
      footer={false}
    >
      <div>
        <p className={styles.message}>Here&apos;s how to get it:</p>
        <HorizontalRule />
        <div className={styles.promoTitleContainer}>
          <SaleTagIcon type="small" isBlackOverride={isBlackOverride} />
          <p className={styles.promoTitle}>{message}</p>
        </div>
        <p className={styles.promoDescription}>
          Click on &#39;Copy promo code&#39; or jot down the code: {promoCode}.
        </p>
        <p className={styles.promoDescription}>
          Enter this code in the &#39;Add a promo code&#39; field at checkout.
        </p>
        {terms && (
          <div className={styles.termsContainer}>
            <TermsAndConditionsAccordion
              terms={terms}
              isGiftWithPurchaseEnabled={true}
            />
          </div>
        )}
        <HorizontalRule />
        <div className={styles.modalFooter}>
          <UnderlinedButton
            className={styles.viewBasketButton}
            onClick={openBasket}
            data-testid="promotional-prompt-basket-button"
          >
            View basket
          </UnderlinedButton>
          <SecondaryButton
            className={styles.copyPromoButton}
            disabled={copyButtonIsClicked}
            id={`copyPromoButton${promoCode}`}
            onClick={() => copyPromoCode()}
          >
            {copyButtonIsClicked ? "Copied" : "Copy promo code"}
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
};
